import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { NoAccess } from "@/components/NoAccess";
import { GuideSheet } from "@/components/kit/GuideSheet";
import { PageBand } from "@/components/kit/PageBand";
import { api, routes } from "@/lib/ppcp";
import { cn } from "@/lib/utils";
import { KIT, PAGES } from "@/constants/testIds";

/** The accented word in each chapter title — the Plattershare house style. */
const ACCENT = {
  "who-this-is-for": "Plan",
  "how-to-use": "Use",
  "balanced-eating-strategy": "Balanced",
  "sugar-and-sweetness": "Sweetness",
  "portion-guide": "Portion",
};

/** Group the authored blocks into panels: at each heading where the page has headings, otherwise by
 *  block family (a table earns its own panel, notes and quotes sit together). Every page therefore
 *  gets real separation instead of one long sheet — and the content itself is never touched. */
const family = (block) =>
  block.type === "table" ? "table" : ["callout", "quote"].includes(block.type) ? "note" : "text";

const toSections = (blocks = []) => {
  const sections = [];
  const hasHeadings = blocks.some((b) => b.type === "heading");

  blocks.forEach((block) => {
    const startNew = hasHeadings
      ? block.type === "heading"
      : !sections.length || family(block) !== sections[sections.length - 1].family;

    if (startNew || !sections.length) {
      sections.push({
        family: family(block),
        heading: block.type === "heading" ? block : null,
        blocks: block.type === "heading" ? [] : [block],
      });
    } else {
      sections[sections.length - 1].blocks.push(block);
    }
  });
  return sections;
};

export default function IntroPage() {
  const { slug, page } = useParams();
  const navigate = useNavigate();
  const [pages, setPages] = useState(null);
  const [programTitle, setProgramTitle] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    api
      .get(`/programs/${slug}`)
      .then((res) => {
        setPages(res.data.program.intro_pages ?? []);
        setProgramTitle(res.data.program.title);
      })
      .catch((err) => (err.response?.status === 403 ? setDenied(true) : setPages([])));
  }, [slug, page]);

  if (denied) return <NoAccess title="Introduction" />;

  const index = pages?.findIndex((p) => p.slug === page) ?? -1;
  const intro = index >= 0 ? pages[index] : null;
  const prev = index > 0 ? pages[index - 1] : null;
  const next = pages && index >= 0 && index < pages.length - 1 ? pages[index + 1] : null;

  // The opening paragraph becomes the chapter's standfirst inside the teal band.
  const all = intro?.blocks ?? [];
  const lead = all[0]?.type === "paragraph" ? all[0] : null;
  const sections = toSections(lead ? all.slice(1) : all);

  return (
    <AppShell
      title={intro?.title ?? "Introduction"}
      slug={slug}
      size="reading"
      crumbs={[
        { label: "My Plans", to: routes.library },
        { label: programTitle ?? "Program", to: routes.program(slug) },
        { label: "Introduction", to: routes.program(slug) },
        { label: intro?.title ?? "Chapter" },
      ]}
    >
      <div data-testid={PAGES.intro} className="contents">
        <PageBand
          eyebrow={pages?.length ? `Chapter ${index + 1} of ${pages.length}` : "Introduction"}
          title={intro?.title}
          accent={ACCENT[page]}
          intro={lead?.text}
        />

        {/* The reading text sits on one white panel; the band and the next-chapter card stay on cream. */}
        <div className="ps-card grid gap-8 p-5 sm:gap-9 sm:p-6">
          {sections.map((section, i) => (
            <section
              key={i}
              data-testid={`intro-section-${i}`}
              className="space-y-5"
            >
              <GuideSheet blocks={section.heading ? [section.heading, ...section.blocks] : section.blocks} />
            </section>
          ))}
        </div>

        {!!pages?.length && (
          <nav className="space-y-5 pb-2 pt-3 sm:space-y-7">
            {/* Plan Overview lives in the persistent bottom navigation, so the chapter shows only its
                own next step. */}
            {/* And the one obvious next step stays the loudest thing on the page. */}
            <button
              data-testid={KIT.introNav(next ? "next" : "start")}
              onClick={() => navigate(next ? routes.intro(slug, next.slug) : routes.days(slug))}
              className="ps-tap group flex w-full items-center justify-between gap-4 rounded-[22px] bg-brand-teal px-5 py-5 text-left shadow-card transition-colors hover:bg-brand-tealDeep sm:px-7 sm:py-6"
            >
              <span className="min-w-0">
                <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-white/65 sm:text-[12px] sm:tracking-[0.2em]">
                  {next ? `Next · chapter ${index + 2} of ${pages.length}` : "You're ready"}
                </span>
                <span className="mt-1 block font-display text-[18px] font-bold leading-snug text-white sm:text-2xl sm:font-semibold">
                  {next ? next.title : "See the full plan"}
                </span>
              </span>
              <span className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-full bg-white/20">
                <ArrowRight className="h-[17px] w-[17px] text-white transition-transform duration-300 group-hover:translate-x-0.5" />
              </span>
            </button>

            <div className="flex flex-wrap items-center justify-between gap-4">
              {prev ? (
                <button
                  data-testid={KIT.introNav("prev")}
                  onClick={() => navigate(routes.intro(slug, prev.slug))}
                  className="ps-tap inline-flex min-h-[44px] min-w-0 max-w-full items-center gap-2 text-[14px] font-semibold text-brand-teal/70 hover:text-brand-teal sm:text-[15px]"
                >
                  <ArrowLeft className="h-4 w-4 shrink-0" />
                  <span className="truncate">{prev.title}</span>
                </button>
              ) : (
                <span />
              )}

              <div data-testid={KIT.chapterDots} className="flex items-center gap-2.5">
                {pages.map((p, i) => (
                  <button
                    key={p.slug}
                    aria-label={p.title}
                    aria-current={i === index}
                    onClick={() => navigate(routes.intro(slug, p.slug))}
                    className={cn(
                      "ps-tap h-3 rounded-full transition-[width,background-color]",
                      i === index ? "w-9 bg-brand-orange" : "w-3 bg-brand-teal/20 hover:bg-brand-teal/40"
                    )}
                  />
                ))}
              </div>
            </div>
          </nav>
        )}
      </div>
    </AppShell>
  );
}
